import { useEffect, useRef } from 'react';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { BuildingsModel } from '../../types';
import TextField from '@mui/material/TextField';

interface PlacesAutocompleteProps {
  onPlaceSelect: (place: google.maps.places.PlaceResult | null) => void;
  control: Control<BuildingsModel>;
  errors: FieldErrors<BuildingsModel>;
}

export default function PlacesAutocomplete({
  onPlaceSelect,
  control,
  errors,
}: PlacesAutocompleteProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const places = useMapsLibrary('places');

  useEffect(() => {
    if (!places || !inputRef.current) return;

    const options = {
      fields: [
        'geometry',
        'name',
        'formatted_address',
        'address_components',
        'adr_address',
      ],
    };

    const autocomplete = new places.Autocomplete(inputRef.current, options);

    // Apply inline styles to the autocomplete results
    const observer = new MutationObserver(() => {
      const autocompleteResults = document.querySelector('.pac-container');
      if (autocompleteResults) {
        (autocompleteResults as HTMLElement).style.zIndex = '1301';
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      onPlaceSelect(place);
    });

    return () => {
      observer.disconnect();
      const pacContainer = document.querySelector('.pac-container');
      if (pacContainer) {
        pacContainer.remove();
      }
    };
  }, [onPlaceSelect, places]);

  return (
    <Controller
      name="address.addressLines.0"
      control={control}
      rules={{
        required: 'Street address is required.',
      }}
      render={({ field }) => (
        <TextField
          required
          sx={{ width: 400 }}
          inputProps={{
            id: 'placesAutocomplete',
          }}
          inputRef={inputRef}
          label="Street Address"
          {...field}
          error={!!errors.address?.addressLines}
          helperText={errors?.address?.addressLines?.[0]?.message || ' '}
        />
      )}
    />
  );
}
